import { Build } from '@stencil/core';
import DOMPurify from 'dompurify';
import {
  ContinueResponse,
  GetChallengeResponse,
  SignInitResponse,
} from '../class/class';
import {
  ActionType,
  Constants,
  CreditCardType,
  Language,
  LevelRequested,
  NotificationType,
  SigningLimitation,
  SigningMethod,
  SourceName,
} from '../enums/const-enums';
import { setCreditCard, setDebitCard } from '../redux/actions/card-action';
import { updateChallengeData } from '../redux/actions/challenge-action';
import { uiReady } from '../redux/actions/config-actions';
import {
  setActiveMethod,
  setAllowedMethods,
} from '../redux/actions/method-action';
import { showNotification } from '../redux/actions/notification-action';
import { showOverlay } from '../redux/actions/overlay-actions';
import { initialStore } from '../redux/store';
import { isDarkMode } from './theming';

export function getSanitizedSummary(
  sourceName: SourceName,
  html: string,
  extraCss: string,
): string {
  if (sourceName === SourceName.IDIN && isDarkMode()) {
    // Since iDIN can't follow the RABOTHM cookie, override the server provided extra css in dark mode
    extraCss =
      '<style>:host,rass-summary-content{color:var(--rds-paragraph-color-text);}</style>';
  } else if (!isDarkMode()) {
    extraCss = ''; // On web we don't need to add the additional css for light mode
  }
  return DOMPurify.sanitize(`${extraCss}${html}` || '', { FORCE_BODY: true });
}

export function postMessageToParent<TMessageData>(
  data: TMessageData,
  options: WindowPostMessageOptions = { targetOrigin: '*' },
): void {
  if (window.parent != window) {
    window.parent.postMessage(data, options);
  }
}

export function getIframeSandboxStatus() {
  if (window.parent === window) return 'no-iframe';
  let frame;
  try {
    frame = window.frameElement;
  } catch (err) {
    frame = null;
  }
  if (frame === null) {
    if (document.domain !== '') return 'unkown'; // Probably 'non-sandboxed'
    if (location.protocol !== 'data:') return 'sandboxed';
    return 'unkown'; // Can be 'sandboxed' on Firefox
  }
  return frame.hasAttribute('sandbox') ? 'sandboxed' : 'non-sandboxed';
}

export const isEventDrivenSigning = location.pathname.endsWith(
  'qsl_auth_method.htm', // This path is only used by the RASS proxy in senses
);

export function handleContinueResponse(
  continueResponse: ContinueResponse,
  signStatus: AppModel.SignStatus,
  code?: string,
): void {
  if (isEventDrivenSigning) {
    if (continueResponse.continue_url) {
      fetch(continueResponse.continue_url).finally(() => {
        postMessageToParent<AppModel.SignStatusEvent>({
          type: 'sign-status',
          value: signStatus,
          code,
        });
      });
    } else {
      postMessageToParent<AppModel.SignStatusEvent>({
        type: 'sign-status',
        value: signStatus,
        code,
      });
    }
    return;
  }
  // delaying the redirect slightly to allow the browser to ensure navigator.sendBeacon() to complete in piano analytics service
  setTimeout(() => {
    continueResponse.redirect();
  }, 1);
}

export function logd(func: () => void): void {
  if (Build.isDev) {
    func();
  }
}

export const browserFeatureDetection = {
  shadowDOM: !!HTMLElement.prototype.attachShadow,
};

export function getUrlParameter(urlParam: string): string {
  return new URL(window.location.href).searchParams.get(urlParam);
}

export function getDefaultMethod(): SigningMethod {
  const methodCookieValue = getCookieValue(Constants.METHOD_COOKIE_NAME);
  if (methodCookieValue === 'rr') {
    return SigningMethod.RANDOM_READER;
  }
  if (methodCookieValue === 'qr') {
    return SigningMethod.QR;
  }
  if (
    methodCookieValue === 'eotp' &&
    location.hostname.includes('rabobank.com')
  ) {
    return SigningMethod.EOTP;
  }
  if (methodCookieValue === 'rs2') {
    return SigningMethod.RABO_SCANNER;
  }
  if (!document.cookie.includes('PNP=')) {
    // The PNP cookie is only set in the Rabo App shell
    return SigningMethod.QR;
  }
  return SigningMethod.RABO_SCANNER;
}

export function getCookieValue(cookieName: string): string {
  const name = `${cookieName}=`;
  try {
    return (
      document.cookie
        .split(';')
        .find((cookie) => cookie.trim().indexOf(name) === 0) || ''
    )
      .trim()
      .substring(name.length);
  } catch {
    return '';
  }
}

export function setCookie(cookieName: string, cookieValue: string): void {
  const cookieDate = new Date();
  cookieDate.setFullYear(cookieDate.getFullYear() + 1);
  let cookie = `${cookieName}=${cookieValue}; path=/; expires=${cookieDate.toUTCString()};`;
  if (location.hostname !== 'localhost') {
    // Add cookie attributes that dont work on localhost in Chrome / Safari, so we can test locally
    const hostNameSegments = location.hostname.split('.');
    const domain = `${hostNameSegments[hostNameSegments.length - 2]}.${hostNameSegments[hostNameSegments.length - 1]}`;
    cookie += ` secure; samesite=none; domain=.${domain};`;
  }
  document.cookie = cookie;
}

export function setCookieIfAbsentAndUrlParameterPresent(
  cookieName: string,
): void {
  const urlParamValue = getUrlParameter(cookieName.toLowerCase());
  if (!getCookieValue(cookieName) && !!urlParamValue) {
    setCookie(cookieName, urlParamValue);
  }
}

export function setDocumentLanguage(language: string): void {
  document.querySelector('html').setAttribute('lang', language);
}

export async function fetchWithTimeout(
  url: URL | RequestInfo,
  opts: RequestInit = {},
  timeout = Constants.REQUEST_TIMEOUT as number,
) {
  const abortController = new AbortController();
  const { signal } = abortController;
  const fetchPromise = fetch(url, {
    ...opts,
    signal,
  });
  // Until AbortSignal.timeout has better browser support, use the fallback solution using a regular timeout
  const timer = setTimeout(() => abortController.abort(), timeout);
  try {
    const result = await fetchPromise;
    clearTimeout(timer);
    return result;
  } catch (e) {
    clearTimeout(timer);
    throw e;
  }
}

export function parseResponse<TData>(
  response: Response,
): Promise<AppModel.HttpResponse<TData>> {
  if (response.redirected) {
    return new Promise((resolve, reject): void => {
      response
        .text()
        .then((text) =>
          resolve({
            status: response.status,
            ok: response.ok,
            url: response.url,
            text: text,
          }),
        )
        .catch((err: Error) =>
          reject({
            status: response.status,
            network_error: err,
          }),
        );
    });
  }

  return new Promise((resolve, reject): void => {
    response
      .json()
      .then((json: TData) =>
        resolve({
          status: response.status,
          ok: response.ok,
          json: json,
        }),
      )
      .catch((err: Error) =>
        reject({
          status: response.status,
          network_error: err,
        }),
      );
  });
}

export function handleResponse<T>(
  response: AppModel.HttpResponse<T>,
): AppModel.HttpResponse<T> {
  if (response.text) {
    // was a redirect or something else, transform to proper error
    throw {
      faultLevel: NotificationType.ERROR,
      fatal: {
        continue_url: response.url,
      },
    };
  }

  if (!response.ok) {
    logd(() => console.warn(`Handled error: ${JSON.stringify(response)}`));
    const errResp: AppModel.HttpResponseErrorJson = {
      status: response.status,
      faultCode: null,
      faultText: null,
      faultLevel:
        response.status >= 400 || response.status <= 451
          ? NotificationType.WARNING
          : NotificationType.ERROR,
    };

    if (response.network_error) {
      // network error while streaming response
      errResp.faultCode = '999';
      errResp.faultText = response.network_error;
    }

    if (response.json) {
      const errorResponse =
        response.json as unknown as AppModel.HttpResponseErrorJson;
      errResp.faultCode = errorResponse.faultCode || errorResponse.faultcode;
      errResp.faultText = errorResponse.faulttext;
      errResp.auth_reference =
        errorResponse.auth_reference || errorResponse.traceId;
      errResp.error_code = errorResponse.error_code || errorResponse.errorCode;

      if (errorResponse.fatal) {
        errResp.fatal = errorResponse.fatal;
      }
    }

    throw errResp;
  }

  return response;
}

export function handleMethodAndChallengeData(
  resp: SignInitResponse | GetChallengeResponse,
): void {
  if (resp.signing_limitation_info) {
    showOverlayWithWarningMessage(resp);
  } else {
    handleMethodAndChallengeDataWithoutMalwareCheck(resp);
  }
}

export function handleMethodAndChallengeDataWithoutMalwareCheck(
  resp: SignInitResponse | GetChallengeResponse,
) {
  initialStore.dispatch(updateChallengeData(resp.challenge_data));
  if (
    initialStore.getState().methods.activeSignMethod !== resp.selected_method
  ) {
    initialStore.dispatch(setActiveMethod(resp.selected_method));
  }
  initialStore.dispatch(
    setAllowedMethods(Object.assign([], resp.usable_methods)),
  );

  if (
    resp.challenge_data.token_info &&
    resp.challenge_data.token_info.objectType === 'credit_card_info'
  ) {
    initialStore.dispatch(
      setCreditCard({
        isCardSwitchAllowed: resp.challenge_data.token_info.switch_allowed,
        isCardValid: true,
        cardNumber: resp.challenge_data.token_info.card_number,
        creditCardSuffix: resp.challenge_data.token_info.credit_card_suffix,
        creditCardType: resp.challenge_data.token_info
          .credit_card_type as CreditCardType,
      }),
    );
  }
  if (
    resp.challenge_data.token_info &&
    resp.challenge_data.token_info.account_number &&
    resp.challenge_data.token_info.account_number !== '' &&
    resp.challenge_data.token_info.card_number &&
    resp.challenge_data.token_info.card_number !== ''
  ) {
    const isCardSwitchAllowed =
      String(resp.challenge_data.token_info.switch_allowed) === 'true';
    initialStore.dispatch(
      setDebitCard({
        accountNumber: resp.challenge_data.token_info.account_number,
        cardNumber: resp.challenge_data.token_info.card_number,
        isCardRemembered: isCardSwitchAllowed
          ? initialStore.getState().card.isCardRemembered
          : false,
        isCardSwitchAllowed: isCardSwitchAllowed,
        isCardValid: true,
      }),
    );
  }
}

function showOverlayWithWarningMessage(
  resp: SignInitResponse | GetChallengeResponse,
) {
  initialStore.dispatch(
    showOverlay({
      resp,
      type: 'warning',
      active: true,
      code: getCodeForSigningLimitationSuccessFlow(
        resp.signing_limitation_info,
      ),
    }),
  );
  initialStore.dispatch(uiReady());
}

function getCodeForSigningLimitationSuccessFlow(
  signingLimitationInfo: AppModel.SigningLimitationInfo,
) {
  if (
    signingLimitationInfo.signing_limitation ===
      SigningLimitation.HIGH_RISK_NOT_ALLOWED &&
    signingLimitationInfo.level_requested !== LevelRequested.HIGH
  ) {
    return '00801';
  }
  if (
    signingLimitationInfo.signing_limitation ===
    SigningLimitation.HIGH_RISK_CHANNEL_SEPARATION
  ) {
    return signingLimitationInfo.level_requested === LevelRequested.HIGH
      ? '00803'
      : '00802';
  }
  return '';
}

function handleError(error: AppModel.HttpResponseErrorJson) {
  if (error.network_error && error.network_error instanceof SyntaxError) {
    const lang = initialStore.getState().config.language;
    const texts: Record<Language, string> = {
      [Language.NL]: 'Systeemfout (999)',
      [Language.EN]: 'System error (999)',
      [Language.PT]: 'System error (999)',
      [Language.ES]: 'System error (999)',
    };
    throw {
      fatal: {
        continue_url: location.hostname.split('.').slice(-2).join('.'),
      },
      faultText: texts[lang],
      errorCode: '999',
    };
  }
  throw {
    faultCode: error.faultCode || '999',
    faultText: error.message || error.faultText,
    fatal: error.fatal,
    traceId: error.auth_reference,
    errorCode: error.error_code,
  };
}

export async function httpRequest<T>(
  url: string,
  data?: object,
): Promise<AppModel.HttpResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const xsrfCookie = getCookieValue(Constants.XSRF_COOKIE_NAME);
  if (xsrfCookie) {
    headers['x-rabo-xsrfp'] = xsrfCookie;
  }

  // Temporary until we can remove the v1 pulse api
  const oamXsrfCookie = getCookieValue('rabo-xsrfp');
  if (oamXsrfCookie) {
    headers['x-oam-xsrfp'] = oamXsrfCookie;
  }

  const opts: RequestInit = {
    credentials: 'include',
    mode: 'cors',
    method: data ? 'POST' : 'GET',
    headers: headers,
  };

  if (data) {
    opts.body = JSON.stringify(data);
  }

  return fetchWithTimeout(url, opts)
    .then(parseResponse)
    .then((response) => handleResponse(response))
    .catch((error: AppModel.HttpResponseErrorJson) =>
      handleError(error),
    ) as Promise<AppModel.HttpResponse<T>>;
}

export function removeSpaces(str: string): string {
  return str ? str.replace(/\s/g, '') : '';
}

export const devServerOrigin = {
  express: 'http://localhost:3333/api/msp/',
};

export function handleErrorResponse(
  error: AppModel.HttpResponseErrorJson,
  timeout?: AppModel.TimeoutTranslation,
): void {
  logd(() => console.warn('Handled error: ', error));
  const code = error.faultCode || error['faultcode'];
  const message =
    (error.faultText || error['faulttext']) + (code ? ` (${code})` : '');
  const malwareLimitSigningErrorCodes = ['860', '861', '862'];
  if (error.fatal) {
    if (isEventDrivenSigning) {
      // On fatal error, dont show the overlay but let senses handle the fatal
      handleContinueResponse(
        new ContinueResponse({
          continue_url: error.fatal.continue_url,
          continue_content: error.fatal.continue_content,
        }),
        'failure',
        code,
      );
      return;
    }
    initialStore.dispatch(
      showOverlay({
        message:
          error.faultText || error['faulttext']
            ? message
            : initialStore.getState().notification.body,
        url: error.fatal.continue_url,
        content: error.fatal.continue_content,
        type: 'error',
        active: true,
      }),
    );
  } else if (code && malwareLimitSigningErrorCodes.some((c) => c === code)) {
    initialStore.dispatch(
      showOverlay({
        message:
          error.faultText || error['faulttext']
            ? message
            : initialStore.getState().notification.body,
        type: 'error',
        active: true,
        code,
      }),
    );
  } else if (error.faultText) {
    initialStore.dispatch(
      showNotification({
        type: error.faultLevel || NotificationType.ERROR,
        header: '',
        body: message,
      }),
    );
  } else if (error.timeout) {
    initialStore.dispatch(
      showNotification({
        header: '',
        body: timeout.text,
        type: timeout.type || NotificationType.ERROR,
      }),
    );
  }

  // set loading to false because otherwise the user gets the impression things are still working even though an error occurred
  initialStore.dispatch(uiReady());
}

export function getBaseUrl(): string {
  if (
    location.hostname !== 'localhost' &&
    location.pathname.endsWith('/assets/html/pre-login.html')
  ) {
    // [TODO] Remove after pre-login testing on verification
    return '/welcome/';
  }
  if (Build.isDev) {
    // Required for our e2e tests as the url is monitored for http status
    return devServerOrigin.express;
  }

  // Add a trailing slash for all root level paths (for example /factoring) or deeper paths that require one (see below array)
  const requiresTrailingSlash = [
    '/afronden-web/piftri/pi/ucp/bulk-credit-transfers',
    '/third-party-payments/psd2-sign-batches',
    '/app3/prelogin',
  ];
  if (
    location.pathname.lastIndexOf('/') === 0 ||
    requiresTrailingSlash.includes(location.pathname)
  ) {
    return location.pathname.replace(/\/$|$/, '/');
  }

  return '';
}

export function isRassSignInDocument(): boolean {
  return document.querySelector('rass-sign') !== null;
}

export function getSignElementById<TElem extends HTMLElement>(
  selector: string,
): TElem {
  return (
    browserFeatureDetection.shadowDOM && isRassSignInDocument()
      ? document.querySelector('rass-sign').shadowRoot
      : document
  ).querySelector<TElem>(`#${selector}`);
}

export function handleBlur(
  userInput: string,
  validityCondition: boolean,
): boolean {
  if (userInput.length > 0) {
    return !validityCondition;
  }
  return false;
}

export function handleEnterKey(
  event: KeyboardEvent,
  validityCondition: boolean,
  buttonSelector?: string,
): boolean {
  if (event.key === 'Enter') {
    if (validityCondition && buttonSelector) {
      const buttonElement =
        getSignElementById<HTMLRdsButtonElement>(buttonSelector);
      if (buttonElement) {
        buttonElement.click();
      }
    }
    return !validityCondition;
  }
  return false;
}

export function isInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

export function focusSignElement(
  selector: string,
  focusOptions: FocusOptions = {},
  timeout?: number,
): void {
  const element = getSignElementById<HTMLElement>(selector);
  if (!element) {
    return;
  }
  if (!focusOptions.preventScroll && !isInViewport(element)) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
  logd(() => console.log('Focus', selector, focusOptions));
  setTimeout(
    () => element.focus(focusOptions),
    typeof timeout === 'number' ? timeout : 250,
  );
}

export function isLogin(): boolean {
  return initialStore.getState().config.sourceName === SourceName.LOGIN;
}

export function getActionType(): ActionType {
  return isLogin() ? ActionType.LOGIN : ActionType.SIGN;
}

export function isMobileScreen(): boolean {
  return window.matchMedia('only screen and (max-width: 760px)').matches;
}

export const sensesCoreApiPrefixKey = (key: string): string =>
  `[Core api]-${key}`;

export const getLocalStorageItem = (key: string): string | null => {
  try {
    // Wrap in try/catch for potential missing storage permission in sandbox mode
    return localStorage.getItem(key);
  } catch {
    logd(() =>
      console.warn(`Could not parse item from storage with key: ${key}`),
    );
    return null;
  }
};

export const setLocalStorageItem = (key: string, value: string): void => {
  try {
    // Wrap in try/catch for potential missing storage permission in sandbox mode
    localStorage.setItem(key, value);
  } catch {
    logd(() => console.warn(`Could not set item in storage with key: ${key}`));
  }
};

export const removeLocalStorageItem = (key: string): void => {
  try {
    // Wrap in try/catch for potential missing storage permission in sandbox mode
    localStorage.removeItem(key);
  } catch {
    logd(() =>
      console.warn(`Could not remove item from storage with key: ${key}`),
    );
  }
};
